import React, { useState } from 'react';
import Panel from '../../components/ui/Panel';
import Button from '../../components/ui/Button';
import Dialog from '../../components/ui/Dialog';

const DialogPage: React.FC = () => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [dialogueOpen, setDialogueOpen] = useState(false);

  return (
    <div>
      <h1 id="overview" className="text-3xl text-osrs-gold font-bold uppercase mb-4">Dialog</h1>
      <p className="text-osrs-yellow mb-8">
        A dialog is a stone window over a dimmed game view. Confirmation uses two stone buttons. An alert uses red title text. NPC dialogue uses the tan chat surface and black body text.
      </p>

      <section id="examples" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">Examples</h2>
        <Panel>
          <div className="flex flex-wrap gap-4">
            <Button variant="secondary" onClick={() => setConfirmOpen(true)}>Confirm</Button>
            <Button variant="danger" onClick={() => setAlertOpen(true)}>Alert</Button>
            <Button onClick={() => setDialogueOpen(true)}>Dialogue</Button>
          </div>
        </Panel>
        <Dialog open={confirmOpen} variant="confirm" title="Are you sure?" onClose={() => setConfirmOpen(false)}>
          <p className="mb-4">Destroy the rune scimitar?</p>
          <div className="flex gap-2">
            <Button onClick={() => setConfirmOpen(false)}>Yes</Button>
            <Button variant="secondary" onClick={() => setConfirmOpen(false)}>No</Button>
          </div>
        </Dialog>
        <Dialog open={alertOpen} variant="alert" title="Warning" onClose={() => setAlertOpen(false)}>
          <p className="mb-4">You cannot undo this.</p>
          <Button variant="danger" onClick={() => setAlertOpen(false)}>OK</Button>
        </Dialog>
        <Dialog open={dialogueOpen} variant="dialogue" title="Hans" onClose={() => setDialogueOpen(false)}>
          <p className="mb-4">I have been waiting here for 20 years.</p>
          <Button onClick={() => setDialogueOpen(false)}>Click here to continue</Button>
        </Dialog>
      </section>

      <section id="variants" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">Variants</h2>
        <Panel>
          <ul className="list-disc list-inside text-osrs-yellow space-y-2 ml-4">
            <li>default. A stone window with a title and a close control.</li>
            <li>confirm. The same window for a yes or no choice.</li>
            <li>alert. Red title text for a warning.</li>
            <li>dialogue. Tan surface for an NPC line.</li>
          </ul>
        </Panel>
      </section>

      <section id="usage-guidelines" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">Usage Guidelines</h2>
        <Panel>
          <h3 id="when-to-use" className="text-xl text-osrs-gold font-bold mb-3">When to Use</h3>
          <ul className="list-disc list-inside text-osrs-yellow space-y-2 ml-4">
            <li>Use confirm before a destructive action.</li>
            <li>Use alert when the player must acknowledge a blocked action.</li>
            <li>Use dialogue for a single NPC line. Escape and the close control both dismiss the window.</li>
          </ul>
        </Panel>
      </section>

      <section id="api-reference" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">API Reference</h2>
        <Panel>
          <table className="w-full text-left text-osrs-yellow">
            <thead>
              <tr className="text-osrs-gold">
                <th className="py-2 px-4">Prop</th>
                <th className="py-2 px-4">Type</th>
                <th className="py-2 px-4">Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="py-2 px-4">open</td>
                <td className="py-2 px-4">boolean</td>
                <td className="py-2 px-4">Renders nothing when false</td>
              </tr>
              <tr>
                <td className="py-2 px-4">title</td>
                <td className="py-2 px-4">string</td>
                <td className="py-2 px-4">Names the dialog</td>
              </tr>
              <tr>
                <td className="py-2 px-4">variant</td>
                <td className="py-2 px-4">default | confirm | alert | dialogue</td>
                <td className="py-2 px-4">Frame treatment</td>
              </tr>
              <tr>
                <td className="py-2 px-4">onClose</td>
                <td className="py-2 px-4">function</td>
                <td className="py-2 px-4">Backdrop click, close control, and Escape</td>
              </tr>
            </tbody>
          </table>
        </Panel>
      </section>
    </div>
  );
};

export default DialogPage;
